import "@/assets/css/vendors/tom-select.css";
import clsx from "clsx";
import { createRef, useEffect, useMemo, useRef } from "react";
import { UseFormRegister } from "react-hook-form";
import TomSelectPlugin from "tom-select";
import {
  RecursivePartial,
  TomInput,
  TomSettings,
} from "tom-select/src/types/index";
import { init, setValue, updateValue } from "./tom-select";

export interface TomSelectElement
  extends HTMLSelectElement,
    Omit<TomInput, keyof HTMLSelectElement | "tomselect"> {
  TomSelect: TomSelectPlugin;
}

export interface TomSelectProps<T extends string | string[]>
  extends React.PropsWithChildren,
    Omit<React.ComponentPropsWithoutRef<"select">, "onChange"> {
  value: T;
  onOptionAdd?: (value: string) => void;
  onChange: (e: {
    target: {
      value: T;
    };
  }) => void;
  options?: RecursivePartial<TomSettings>;
  getRef?: (el: TomSelectElement) => void;
  register?: UseFormRegister<any>;
  name: string;
}

function TomSelect<T extends string | string[]>({
  className = "",
  options = {},
  value,
  onOptionAdd = () => {},
  onChange = () => {},
  getRef = () => {},
  register,
  name,
  children,
  disabled,
  ...computedProps
}: TomSelectProps<T>) {
  const props = {
    className: className,
    options: options,
    value: value,
    onOptionAdd: onOptionAdd,
    onChange: onChange,
    getRef: getRef,
    register: register,
    name: name,
  };
  const initialRender = useRef(true);
  const tomSelectRef = createRef<TomSelectElement>();
  const previousValueRef = useRef(props.value);
  const tomSelectInstanceRef = useRef<TomSelectPlugin | null>(null);

  useEffect(() => {
    if (
      props.value &&
      (!Array.isArray(props.value) || props.value.length > 0)
    ) {
      previousValueRef.current = props.value;
    }
  }, [props.value]);

  const handleBackspace = (instance: TomSelectPlugin) => {
    if (previousValueRef.current) {
      instance.addItem(previousValueRef.current as string);
      instance.refreshItems();
      instance.refreshOptions(false);
      props.onChange({
        target: {
          value: previousValueRef.current as T,
        },
      });
    }
  };

  const computedOptions = useMemo(() => {
    let options: TomSelectProps<T>["options"] = {
      ...props.options,
      plugins: {
        dropdown_input: {},
        ...props.options.plugins,
      },
      onInitialize: function (this: TomSelectPlugin) {
        // simply block backspace when the input is empty/near-empty;
        // do not attempt to alter the value at all.
        if (this.control_input) {
          // block every backspace press inside the main control input. the
          // user asked for "do nothing" when backspace is pressed, so we
          // just swallow the event completely regardless of the current text.
          this.control_input.addEventListener(
            "keydown",
            (e: KeyboardEvent) => {
              // only block if the input is already empty – allow normal
              // deletion when there's text present
              if (e.key === "Backspace" && this.control_input.value === "") {
                e.preventDefault();
                e.stopImmediatePropagation();

              // handleBackspace(this);

              }
            },
            { capture: true },
          );
        }

        // also intercept the dropdown search input; prevents backspacing
        // from clearing the current selection when the search field is
        // empty.
        if (this.dropdown_input) {
          // same for the dropdown search field
          this.dropdown_input.addEventListener(
            "keydown",
            (e: KeyboardEvent) => {
              if (e.key === "Backspace" && this.dropdown_input.value === "") {
                e.preventDefault();
                e.stopImmediatePropagation();
              }
            },
            { capture: true },
          );
        }
      },
      onItemRemove: function (value: string) {
        if (previousValueRef.current) {
          setTimeout(() => {
            if (tomSelectInstanceRef.current && !tomSelectInstanceRef.current.getValue()) {
              handleBackspace(tomSelectInstanceRef.current);
            }
          }, 0);
          return false;
        }
      },
      onType: function (str: string) {
        if (
          str === "" &&
          previousValueRef.current &&
          tomSelectInstanceRef.current
        ) {
          handleBackspace(tomSelectInstanceRef.current);
        }
      },
      onDropdownClose: function (this: TomSelectPlugin) {
        this.blur();
      },
    };

    if (Array.isArray(props.value)) {
      options = {
        persist: false,
        create: true,
        onDelete: function (values: string[]) {
          return confirm(
            values.length > 1
              ? "Are you sure you want to remove these " +
                  values.length +
                  " items?"
              : 'Are you sure you want to remove "' + values[0] + '"?',
          );
        },
        ...options,
        plugins: {
          remove_button: {
            title: "Remove this item",
          },
          ...options.plugins,
        },
      };
    }

    return options;
  }, [props.options]);

  useEffect(() => {
    if (tomSelectRef.current) {
      props.getRef(tomSelectRef.current);

      if (initialRender.current) {
        // Unique attribute
        tomSelectRef.current.setAttribute(
          "data-id",
          "_" + Math.random().toString(36).substr(2, 9),
        );

        // Clone the select element to prevent tom select remove the original element
        const clonedEl = tomSelectRef.current.cloneNode(
          true,
        ) as TomSelectElement;

        // Save initial classnames
        const classNames = tomSelectRef.current?.getAttribute("class");
        classNames && clonedEl.setAttribute("data-initial-class", classNames);

        // Hide original element
        tomSelectRef.current?.parentNode &&
          tomSelectRef.current?.parentNode.appendChild(clonedEl);
        tomSelectRef.current.setAttribute("hidden", "true");

        // Initialize tom select
        setValue(clonedEl, props);
        const instance = init(
          tomSelectRef.current,
          clonedEl,
          props,
          computedOptions,
        );
        tomSelectInstanceRef.current = instance as any;

        initialRender.current = false;
      } else {
        const clonedEl = document.querySelectorAll(
          `[data-id='${tomSelectRef.current.getAttribute(
            "data-id",
          )}'][data-initial-class]`,
        )[0] as TomSelectElement;

        updateValue(
          tomSelectRef.current,
          clonedEl,
          props.value,
          props,
          computedOptions,
        );
      }
    }
  }, [tomSelectRef, props.value, props.className]);

  useEffect(() => {
    if (tomSelectInstanceRef.current) {
      if (disabled) {
        tomSelectInstanceRef.current.disable();
      } else {
        tomSelectInstanceRef.current.enable();
      }
    }
  }, [disabled]);

  return (
    <select
      {...computedProps}
      {...(register && register(name))}
      ref={tomSelectRef}
      value={props.value}
      onChange={(e) => {
        props.onChange({
          target: {
            value: e.target.value as T,
          },
        });
      }}
      className={clsx(["tom-select", props.className])}
    >
      {children}
    </select>
  );
}

export default TomSelect;
